import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { projectsAPI } from '../lib/api';
import { AppHeader } from '../components/layout/AppHeader';
import { StatusBadge } from '../components/StatusBadge';
import { StatusPipeline } from '../components/StatusPipeline';
import { EventTimeline } from '../components/EventTimeline';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Skeleton } from '../components/ui/skeleton';
import { Separator } from '../components/ui/separator';
import { Badge } from '../components/ui/badge';
import {
  ArrowLeft, Download, RotateCcw, Play, Pause, Edit3, Check,
  X, Loader2, Clock, User, Mic, FileText, Volume2, Video,
  AlertCircle, Wand2, ChevronDown
} from 'lucide-react';
import { toast } from 'sonner';

export default function ProjectDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingScript, setEditingScript] = useState(false);
  const [editedScript, setEditedScript] = useState('');
  const [retrying, setRetrying] = useState(false);
  const [generatingAudio, setGeneratingAudio] = useState(false);
  const [renderingVideo, setRenderingVideo] = useState(false);
  const [showEvents, setShowEvents] = useState(false);

  const fetchProject = useCallback(async () => {
    try {
      const res = await projectsAPI.get(id);
      setProject(res.data.project);
    } catch (err) {
      toast.error('Failed to load project');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchProject();
  }, [fetchProject]);

  // Poll for updates when project is in an active state
  useEffect(() => {
    if (!project) return;
    const activeStatuses = ['script_generating', 'video_rendering'];
    if (!activeStatuses.includes(project.status)) return;

    const interval = setInterval(fetchProject, 5000);
    return () => clearInterval(interval);
  }, [project?.status, fetchProject]);

  const handleRetry = async () => {
    setRetrying(true);
    try {
      await projectsAPI.retryVideo(id);
      toast.success('Retrying...');
      setTimeout(fetchProject, 2000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Retry failed');
    } finally {
      setRetrying(false);
    }
  };

  const handleApproveScript = async () => {
    try {
      await projectsAPI.approveScript(id, { script_text: editedScript });
      setEditingScript(false);
      toast.success('Script updated');
      fetchProject();
    } catch (err) {
      toast.error('Failed to update script');
    }
  };

  const handleGenerateAudio = async () => {
    setGeneratingAudio(true);
    try {
      await projectsAPI.generateAudio(id);
      toast.success('Audio generation started');
      setTimeout(fetchProject, 3000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start audio generation');
    } finally {
      setGeneratingAudio(false);
    }
  };

  const handleRenderVideo = async () => {
    setRenderingVideo(true);
    try {
      await projectsAPI.renderVideo(id);
      toast.success('Video rendering started! This may take 2-4 minutes.');
      setTimeout(fetchProject, 3000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start video rendering');
    } finally {
      setRenderingVideo(false);
    }
  };

  const handleDownload = () => {
    // Prefer HeyGen CDN URL (works everywhere), fallback to local download
    const cdnUrl = project?.video_url;
    if (cdnUrl) {
      window.open(cdnUrl, '_blank');
    } else if (project?.video_filename) {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      window.open(`${backendUrl}/api/download/video/${project.video_filename}`, '_blank');
    } else if (project?.local_video_url) {
      window.open(project.local_video_url, '_blank');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <AppHeader />
        <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Skeleton className="h-8 w-48 mb-4" />
          <Skeleton className="h-12 w-full mb-6 rounded-2xl" />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Skeleton className="h-96 rounded-2xl" />
            <Skeleton className="h-96 rounded-2xl" />
          </div>
        </main>
      </div>
    );
  }

  if (!project) return null;

  const isActive = ['script_generating', 'video_rendering'].includes(project.status);
  const isFailed = project.status === 'failed';
  const isCompleted = project.status === 'completed';
  const hasScript = !!project.script_text;
  const hasAudio = !!project.audio_url;
  const hasVideo = !!(project.video_url || project.local_video_url);

  const modeLabel = { idea: 'Idea mode', script: 'Script mode', upload: 'Upload mode' };

  return (
    <div className="min-h-screen bg-background app-noise">
      <AppHeader />

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 relative z-10">
        {/* Back button + header */}
        <div className="flex items-center gap-3 mb-8">
          <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} className="rounded-xl">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="font-heading text-2xl font-bold tracking-tight truncate">{project.name}</h1>
            <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
              <span>{modeLabel[project.input_mode] || 'Idea'}</span>
              <span>&middot;</span>
              <span>{project.duration_mode || '60'}s</span>
              <span>&middot;</span>
              <span>{new Date(project.created_at).toLocaleDateString()}</span>
            </div>
          </div>
          <StatusBadge status={project.status} />
        </div>

        {/* Status pipeline */}
        <Card className="rounded-xl mb-6 p-4 shadow-[0_1px_0_rgba(15,23,42,0.04),0_14px_30px_rgba(15,23,42,0.06)]">
          <StatusPipeline status={project.status} />
          {isActive && (
            <div className="flex items-center gap-2 mt-3 text-sm text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>
                {project.status === 'script_generating' && 'Generating script...'}
                {project.status === 'video_rendering' && 'Rendering video... This may take 2-4 minutes.'}
              </span>
            </div>
          )}
          {isFailed && (
            <div className="flex items-center gap-3 mt-3">
              <div className="flex items-center gap-2 text-sm text-red-600">
                <AlertCircle className="w-4 h-4" />
                <span>{project.error || 'An error occurred'}</span>
              </div>
              <Button
                variant="destructive"
                size="sm"
                onClick={handleRetry}
                disabled={retrying}
                className="rounded-xl gap-1"
                data-testid="project-detail-retry-stage-button"
              >
                {retrying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
                Retry
              </Button>
            </div>
          )}
        </Card>

        {/* Two column layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left: Script + Events */}
          <div className="space-y-6">
            {/* Script card */}
            <Card className="rounded-2xl">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <FileText className="w-4 h-4" /> Script
                  </CardTitle>
                  <div className="flex gap-2">
                    {hasScript && !editingScript && project.status === 'script_ready' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setEditedScript(project.script_text);
                          setEditingScript(true);
                        }}
                        className="rounded-lg gap-1 text-xs"
                      >
                        <Edit3 className="w-3 h-3" /> Edit
                      </Button>
                    )}
                    {!hasScript && !isActive && !isFailed && project.input_mode === 'idea' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={async () => {
                          try {
                            await projectsAPI.generateScript(id);
                            toast.success('Script generation started');
                            setTimeout(fetchProject, 3000);
                          } catch (err) {
                            toast.error('Failed to start script generation');
                          }
                        }}
                        className="rounded-lg gap-1 text-xs"
                        data-testid="script-generate-button"
                      >
                        <Wand2 className="w-3 h-3" /> Generate
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {project.status === 'script_generating' ? (
                  <div className="flex items-center gap-2 py-8 justify-center text-muted-foreground">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span className="text-sm">Generating script...</span>
                  </div>
                ) : editingScript ? (
                  <div className="space-y-3">
                    <Textarea
                      value={editedScript}
                      onChange={(e) => setEditedScript(e.target.value)}
                      className="rounded-xl min-h-[200px]"
                    />
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">
                        {editedScript.trim().split(/\s+/).filter(Boolean).length} words
                      </span>
                      <div className="flex gap-2">
                        <Button variant="ghost" size="sm" onClick={() => setEditingScript(false)} className="rounded-lg gap-1">
                          <X className="w-3 h-3" /> Cancel
                        </Button>
                        <Button size="sm" onClick={handleApproveScript} className="rounded-lg gap-1">
                          <Check className="w-3 h-3" /> Save
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : hasScript ? (
                  <div className="space-y-3">
                    {project.script_json?.title && (
                      <h3 className="font-heading font-semibold text-base">{project.script_json.title}</h3>
                    )}
                    <p className="text-sm leading-relaxed whitespace-pre-wrap">{project.script_text}</p>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                      <span>{project.script_text.trim().split(/\s+/).filter(Boolean).length} words</span>
                      {project.script_json?.estimated_word_count && (
                        <span>Target: {project.script_json.estimated_word_count} words</span>
                      )}
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-8">No script generated yet</p>
                )}
              </CardContent>
            </Card>

            {/* Actions for manual pipeline steps */}
            {hasScript && !hasVideo && !isActive && !isFailed && (
              <Card className="rounded-2xl p-4">
                <Button
                  onClick={handleRenderVideo}
                  disabled={renderingVideo}
                  className="w-full rounded-xl gap-2"
                  data-testid="render-video-button"
                >
                  {renderingVideo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Video className="w-4 h-4" />}
                  Render video
                </Button>
              </Card>
            )}

            {/* Event timeline */}
            <Card className="rounded-2xl">
              <CardHeader className="pb-3">
                <button
                  onClick={() => setShowEvents(!showEvents)}
                  className="flex items-center justify-between w-full"
                >
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <Clock className="w-4 h-4" /> Event log
                  </CardTitle>
                  <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${showEvents ? 'rotate-180' : ''}`} />
                </button>
              </CardHeader>
              {showEvents && (
                <CardContent>
                  <EventTimeline events={project.job_events} />
                </CardContent>
              )}
            </Card>
          </div>

          {/* Right: Media + Info */}
          <div className="space-y-6">
            {/* Video player */}
            <Card className="rounded-2xl overflow-hidden">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <Video className="w-4 h-4" /> Video
                  </CardTitle>
                  {project.engine_used && (
                    <Badge
                      variant="secondary"
                      className="text-[10px] px-2 py-0.5 font-mono font-semibold uppercase tracking-wider"
                      data-testid="heygen-model-badge"
                    >
                      {project.engine_used === 'avatar_iv' ? 'v4' : 'v3'}
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {hasVideo ? (
                  <div className="space-y-3">
                    <div className="relative aspect-video bg-black rounded-xl overflow-hidden">
                      <video
                        src={project.video_url || project.local_video_url}
                        controls
                        className="w-full h-full"
                        poster={project.thumbnail_url}
                        data-testid="project-detail-video-player"
                      />
                      {project.engine_used && (
                        <div
                          className="absolute top-2 right-2 bg-black/70 backdrop-blur-sm text-white px-2 py-0.5 rounded-md text-[10px] font-mono font-bold uppercase tracking-wider"
                          data-testid="video-engine-overlay-badge"
                        >
                          {project.engine_used === 'avatar_iv' ? 'v4' : project.engine_used === 'avatar_v3_fallback' ? 'v3' : project.engine_used}
                        </div>
                      )}
                    </div>
                    <Button
                      onClick={handleDownload}
                      className="w-full rounded-xl gap-2"
                      data-testid="project-detail-download-button"
                    >
                      <Download className="w-4 h-4" /> Download MP4
                    </Button>
                  </div>
                ) : project.status === 'video_rendering' ? (
                  <div className="aspect-video bg-muted rounded-xl flex flex-col items-center justify-center gap-3">
                    <Loader2 className="w-8 h-8 animate-spin text-primary" />
                    <p className="text-sm text-muted-foreground">Rendering video...</p>
                    <p className="text-xs text-muted-foreground">This may take 2-4 minutes</p>
                  </div>
                ) : (
                  <div className="aspect-video bg-muted rounded-xl flex flex-col items-center justify-center gap-2">
                    <Video className="w-8 h-8 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">Video not generated yet</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Project info */}
            <Card className="rounded-2xl">
              <CardHeader className="pb-3">
                <CardTitle className="font-heading text-lg">Project info</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {[
                    { label: 'Mode', value: modeLabel[project.input_mode] || 'Idea' },
                    { label: 'Duration', value: `${project.duration_mode || '60'} seconds` },
                    { label: 'Tone', value: project.tone || 'Professional' },
                    { label: 'Avatar', value: project.avatar_id || 'Not selected' },
                    { label: 'Voice', value: project.voice_id || 'Not selected' },
                    { label: 'Engine', value: project.engine_used === 'avatar_iv' ? 'HeyGen Avatar v4' : (project.engine_used === 'avatar_v3_fallback' || project.engine_used === 'avatar_v3') ? 'HeyGen Avatar v3' : project.engine_used || '-' },
                    { label: 'Background', value: project.background_type === 'green_screen' ? 'Green Screen' : 'Normal (White)' },
                    { label: 'Created', value: new Date(project.created_at).toLocaleString() },
                    { label: 'Updated', value: project.updated_at ? new Date(project.updated_at).toLocaleString() : '-' },
                  ].map((item) => (
                    <div key={item.label} className="flex items-center justify-between py-1.5 border-b last:border-0">
                      <span className="text-sm text-muted-foreground">{item.label}</span>
                      <span className="text-sm font-medium truncate max-w-[200px]">{item.value}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
